/**
 * Problem types (uwagi) an admin marked with the "blinking" option — an item carrying
 * such a remark pulses in the production tables.
 *
 * The remark is stored on the item as plain text ("Name" or "Name - description"),
 * so matching is done by name. The list is small and rarely changes, hence it is
 * fetched once per page load and shared by every table that needs it.
 */

import { useEffect, useState } from 'react';
import api from '../services/api';

let namesCache: Promise<string[]> | null = null;

function fetchBlinkingNames(): Promise<string[]> {
  if (!namesCache) {
    namesCache = api
      .get('/problem-types')
      .then((res) =>
        (res.data || [])
          .filter((pt: any) => pt.is_blinking)
          .map((pt: any) => String(pt.name))
      )
      .catch(() => {
        // Do not cache a failed request — the next table retries.
        namesCache = null;
        return [];
      });
  }
  return namesCache;
}

export function matchesBlinkingProblemType(
  note: string | null | undefined,
  blinkingNames: string[]
): boolean {
  const text = (note ?? '').trim();
  if (!text || blinkingNames.length === 0) return false;
  return blinkingNames.some((name) => text === name || text.startsWith(`${name} - `));
}

/**
 * Returns a predicate telling whether a remark text belongs to a blinking problem type.
 */
export function useBlinkingProblemTypes(): (note: string | null | undefined) => boolean {
  const [blinkingNames, setBlinkingNames] = useState<string[]>([]);

  useEffect(() => {
    let active = true;
    fetchBlinkingNames().then((names) => {
      if (active) setBlinkingNames(names);
    });
    return () => {
      active = false;
    };
  }, []);

  return (note) => matchesBlinkingProblemType(note, blinkingNames);
}
